import React, { useState, useEffect, useRef } from 'react';
import './../../../Styles/Inbox/Inbox.css';
import SideMenuBar from '../../../Components/Layouts/SideMenuBar';
import TopMenuBar from '../../../Components/Layouts/TopMenuBar';
import RightSideBar from '../../../Components/Layouts/RightSideBar';
import RateOrder from './RateOrderModel';
import { useTranslation } from 'react-i18next';
import image1 from '../../../assets/Image1.png';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';
import axiosUserInstance from '../../../axiosUserInstance';
import axiosVendorInstance from '../../../axiosVendorInstance';
import { useCart } from '../../../CartProvider';
import OrderComplete from '../../../assets/OrderCompleteIcon.svg';
import OrderPending from '../../../assets/OrderPendingIcon.svg';
import OrderOngoing from '../../../assets/OrderOngoingIcon.svg';
import LocationIcon from '../../../assets/locationImg.svg';
import { IoPlayOutline } from 'react-icons/io5';
import Loader from '../../../Components/Loader/Loader';
import PreparingIcon from '../../../assets/svg/preparing';
import OrderPlaceIcon from '../../../assets/svg/orderPlace';
import { SEO } from '../../../Utils/Constant';
import { Helmet } from 'react-helmet';

function Inbox() {
    const { t } = useTranslation();
    const [previousOrders, setPreviousOrders] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rateOrder, setRateOrder] = useState([]);
    const [vendorID, setVendorID] = useState();
    const [openRateOrder, setOpenRateOrder] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { fetchCartCount } = useCart();
    const videoRefs = useRef({});
    const [playingIndex, setPlayingIndex] = useState(null);

    const handlePlay = (e, index) => {
        e.stopPropagation();

        Object.entries(videoRefs.current).forEach(([key, video]) => {
            if (parseInt(key) !== index && video && !video.paused) {
                video.pause();
            }
        });

        const currentVideo = videoRefs.current[index];
        if (currentVideo) {
            currentVideo.play();
            setPlayingIndex(index);
        }
    };

    const fetchPreviousOrders = async () => {
        try {
            setLoading(true);
            const response = await axiosUserInstance.get(
                `/orders/previous-orders-with-vendor?pageNo=${pageNo}&pageSize=4`
            );
            setPreviousOrders(response?.data?.orders || []);
            setTotalPages(response?.data?.pagination?.totalPages || 1);
            setPageNo(response?.data?.pagination?.currentPage || 1);
        } catch (error) {
            console.error('Error fetching orders:', error);
            setPreviousOrders([]);
            setTotalPages(1);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };

    useEffect(() => {
        fetchPreviousOrders();
    }, [pageNo]);

    const handlePageChange = (event, value) => {
        setPageNo(value);
    };

    const handleOpenRateOrder = (id) => {
        const filteredData = previousOrders.find(
            (order) => order.OrderInfo?.OrderID === id
        );

        if (filteredData) {
            const menuItems = filteredData.OrderInfo.OrderInfo.flatMap(
                (order) => order.MenuItems
            );
            const restaurantVendorId = filteredData.OrderInfo.vendorID;
            setRateOrder(menuItems);
            setVendorID(restaurantVendorId);
            setOpenRateOrder(true);
        } else {
            setRateOrder([]);
        }

        setOpenRateOrder(true);
    };

    const handleCloseRateOrder = () => setOpenRateOrder(false);

    const handleOrderDetails = (orderId) => {
        navigate(`/orders/${orderId}`);
    };

    const handleOrderAgain = async (e, orderId) => {
        e.stopPropagation();

        try {
            setLoading(true);
            const response = await axiosVendorInstance.get(
                `/order/reorder/ordersdata?order_id=${orderId}`,
                { skipAuth: true }
            );
            if (response?.status == 200) {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
                navigate('/cart');
                await fetchCartCount();
            }
        } catch (error) {
            setLoading(false);
            console.error('Error fetching previous orders again', error);
        }
    };

    const getOrderStatusComponent = (status) => {
        switch (status) {
        case 'Placed':
            return (
                <div className='status-icon-text'>
                    <OrderPlaceIcon />
                    <p className='order-status placed'> Order Placed</p>
                </div>
            );
        case 'Accepted':
            return (
                <div className='status-icon-text'>
                    <PreparingIcon />
                    <p className='order-status preparing'> Order Preparing</p>
                </div>
            );
        case 'Delivered':
            return (
                <div className='status-icon-text'>
                    <img src={OrderComplete} alt='complete' />
                    <p className='order-status complete'> Order Delivered</p>
                </div>
            );
        case 'Out for Delivery':
            return (
                <div className='status-icon-text'>
                    <PreparingIcon />
                    <p className='order-status preparing'> Order Preparing</p>
                </div>
            );
        case 'In Transit':
            return (
                <div className='status-icon-text'>
                    <img src={OrderOngoing} alt='ongoing' />
                    <p className='order-status ongoing'> Out For Delivery</p>
                </div>
            );
        case 'Rejected':
            return (
                <div className='status-icon-text'>
                    <img src={OrderPending} alt='pending' />
                    <p className='order-status pending'> Order Rejected</p>
                </div>
            );
        default:
            return (
                <div className='status-icon-text'>
                    <img src={OrderPending} alt='pending' />
                    <p className='order-status pending'> Order Pending</p>
                </div>
            );
        }
    };

    return (
        <div>
            <Helmet>
                <title>{SEO.ORDER_INFO_PAGE.TITLE}</title>
                <meta name="description" content={SEO.ORDER_INFO_PAGE.DESCRIPTION} />
                <meta name="keywords" content={SEO.ORDER_INFO_PAGE.KEYWORDS} />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={SEO.ORDER_INFO_PAGE.CANONICAL} />

                <meta property="og:title" content={SEO.ORDER_INFO_PAGE.TITLE} />
                <meta property="og:description" content={SEO.ORDER_INFO_PAGE.DESCRIPTION} />
                <meta property="og:type" content={SEO.ORDER_INFO_PAGE.OG.TYPE} />
                <meta property="og:url" content={SEO.ORDER_INFO_PAGE.OG.URL} />
                <meta property="og:site_name" content={SEO.ORDER_INFO_PAGE.OG.SITE_NAME} />
                <meta property="og:image" content='https://omyfood.io/OMF_logo_bg.png' />

                <meta name="twitter:image" content='https://omyfood.io/OMF_logo_bg.png' />
                <meta name="twitter:title" content={SEO.ORDER_INFO_PAGE.TITLE} />
                <meta name="twitter:description" content={SEO.ORDER_INFO_PAGE.DESCRIPTION} />
                <meta
                    name="twitter:card"
                    content='summary_large_image'
                />

                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        'name': SEO.ORDER_INFO_PAGE.TITLE,
                        'description': SEO.ORDER_INFO_PAGE.DESCRIPTION,
                        'url': SEO.ORDER_INFO_PAGE.CANONICAL,
                        'image': 'https://omyfood.io/OMF_logo_bg.png',
                        'mainEntityOfPage': {
                            '@type': 'WebPage',
                            '@id': SEO.ORDER_INFO_PAGE.CANONICAL
                        },
                        'publisher': {
                            '@type': 'Organization',
                            'name': SEO.ORDER_INFO_PAGE.OG.SITE_NAME,
                            'logo': {
                                '@type': 'ImageObject',
                                'url': 'https://omyfood.io/OMF_logo_bg.png'
                            }
                        }
                    })}
                </script>
            </Helmet>
            {loading && (
                <div>
                    <Loader />
                </div>
            )}
            <div className='topmenubar'>
                <SideMenuBar />
                <div className='top-bar-mobile'>
                    <TopMenuBar backIconVisible={false} />
                </div>
            </div>

            <div className='order-card-main-container'>
                <h1 className='previous-orders-heading'>Previous Orders</h1>

                {!loading && previousOrders?.length > 0 ? (
                    <>
                        <div className='order-list-container'>
                            {previousOrders?.map((order, index) => (
                                <div
                                    key={index}
                                    className='order-card-container'
                                    onClick={() => handleOrderDetails(order?.OrderInfo?.OrderID)}
                                >
                                    <div className='order-list-media-wrapper'>
                                        {order?.OrderInfo?.OrderInfo?.[0]?.MenuItems?.[0]?.MenuItemAvatarKey?.match(
                                            /\.(mp4|webm|ogg)$/i
                                        ) ? (
                                                <div className='order-list-video-container1'>
                                                    <video
                                                        src={
                                                            order?.OrderInfo?.OrderInfo?.[0]?.MenuItems?.[0]
                                                                ?.MenuItemAvatarKey
                                                        }
                                                        muted
                                                        playsInline
                                                        // eslint-disable-next-line react/no-unknown-property
                                                        webkit-playsinline='true'
                                                        preload='metadata'
                                                        className='order-list-food-video'
                                                        ref={(el) => (videoRefs.current[index] = el)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const video = videoRefs.current[index];
                                                            if (video && !video.paused) {
                                                                video.pause();
                                                                setPlayingIndex(null);
                                                            }
                                                        }}
                                                        onEnded={() => setPlayingIndex(null)}
                                                        onError={(e) => {
                                                            e.target.poster = image1;
                                                        }}
                                                    />
                                                    {playingIndex !== index && (
                                                        <button
                                                            onClick={(e) => handlePlay(e, index)}
                                                            className='play-btn'
                                                        >
                                                            <IoPlayOutline color='white' fontSize={34} />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <img
                                                    className='order-list-Food-image'
                                                    src={
                                                        order?.OrderInfo?.OrderInfo?.[0]?.MenuItems?.[0]
                                                            ?.MenuItemAvatarKey || image1
                                                    }
                                                    alt='Food img'
                                                    onError={(e) => {
                                                        e.target.src = image1;
                                                    }}
                                                />
                                            )}
                                    </div>
                                    <div className='restaurant-information-container'>
                                        <div>
                                            <div className='restaurant-title-container'>
                                                <h4 className='restaurant-title'>
                                                    {order?.OrderInfo?.restaurantName ||
                            'Unknown Restaurant'}
                                                </h4>
                                                <div className='address-container11'>
                                                    <img src={LocationIcon} alt='location' />
                                                    <p className='restaurant-description1'>
                                                        {order?.OrderInfo?.address}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='break-line'></div>
                                            <div className='order-quantity-status-container'>
                                                <div className='item-status-info'>
                                                    <div className='total-item-text-container'>
                                                        <p className='total-item-text'>Total item : </p>
                                                        <p className='total-item-quantity'>
                                                            {order?.OrderInfo?.OrderInfo?.length}
                                                        </p>
                                                    </div>
                                                    <div className='status-text-container'>
                                                        <p className='status-text'>Status : </p>
                                                        {getOrderStatusComponent(
                                                            order?.OrderInfo?.OrderStatus
                                                        )}
                                                    </div>
                                                </div>
                                                <div className='order-prize'>
                                                    <h4>${order?.OrderInfo?.FinalPrize?.toFixed(2) || 0}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='price-and-btns-container'>
                                            <div className='two-btns'>
                                                <button
                                                    className='btn-rate-order'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenRateOrder(order?.OrderInfo?.OrderID);
                                                    }}
                                                    disabled={
                                                        order?.OrderInfo?.OrderStatus == 'Delivered'
                                                            ? false
                                                            : true
                                                    }
                                                    style={{
                                                        cursor:
                              order?.OrderInfo?.OrderStatus != 'Delivered'
                                  ? 'not-allowed'
                                  : 'pointer',
                                                        opacity:
                              order?.OrderInfo?.OrderStatus != 'Delivered'
                                  ? 0.7
                                  : 1,
                                                    }}
                                                >
                                                    {t('inbox.rateorder-btn')}
                                                </button>
                                                {openRateOrder && (
                                                    <RateOrder
                                                        rateOrder={rateOrder}
                                                        vendorID={vendorID}
                                                        open={openRateOrder}
                                                        handleClose={handleCloseRateOrder}
                                                    />
                                                )}
                                                <button
                                                    className='btn-reorder'
                                                    onClick={(e) =>
                                                        handleOrderAgain(e, order?.OrderInfo?.OrderID)
                                                    }
                                                >
                                                    {t('inbox.Reorder-btn')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Stack
                            spacing={2}
                            className='pagination-controls'
                            style={{ marginTop: '15px' }}
                        >
                            <Pagination
                                count={totalPages}
                                page={pageNo}
                                onChange={handlePageChange}
                                showFirstButton
                                showLastButton
                                defaultPage={1}
                                siblingCount={0}
                            />
                        </Stack>
                    </>
                ) : (
                    !loading && <h5 className='no-data-found'>No Orders Found</h5>
                )}
            </div>

            <RightSideBar />
        </div>
    );
}

export default Inbox;
